/**
 * Citron slider: the shared engine behind the hero (sections/citron-hero.liquid)
 * and the promo banner (sections/citron-promo-banner.liquid).
 *
 * Vanilla, no dependencies, loaded with `defer`. Cross-fades stacked slides;
 * the CSS does the animation, this only moves the .is-active class around.
 * Markup contract (all but [data-ctr-slide] optional):
 *   [data-ctr-slide]   each slide; the one with .is-active shows first
 *   [data-ctr-live]    the slides wrapper: swipe area and aria-live region
 *   [data-ctr-dot]     pagination buttons, in slide order
 *   [data-ctr-sync]    a wrapper holding one child per slide, in slide order,
 *                      kept in step with the active slide (CTAs, feature rows)
 *   [data-ctr-prev]    previous-slide button
 *   [data-ctr-next]    next-slide button
 *   [data-ctr-toggle]  pause/play button
 *   [data-ctr-notch]   element to keep clear of the floating WhatsApp button
 *   [data-ctr-dormant] image wrappers not downloaded until their slide is near
 *
 * Autoplay pauses for any of these reasons and resumes when all clear:
 * hover, keyboard focus, a finger on the slider, the tab hidden, the slider
 * scrolled out of view, the theme editor selecting a slide, or the pause
 * button. With prefers-reduced-motion the slideshow starts paused.
 */
(() => {
  if (customElements.get('citron-slider')) return;

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const SWIPE_MIN = 40;
  const idle = window.requestIdleCallback || ((cb) => setTimeout(cb, 1200));

  class CitronSlider extends HTMLElement {
    connectedCallback() {
      this.slides = [...this.querySelectorAll('[data-ctr-slide]')];
      this.groups = [...this.querySelectorAll('[data-ctr-sync]')].map((group) => [...group.children]);
      this.dots = [...this.querySelectorAll('[data-ctr-dot]')];
      this.toggle = this.querySelector('[data-ctr-toggle]');
      this.live = this.querySelector('[data-ctr-live]');
      this.notch = this.querySelector('[data-ctr-notch]');
      this.count = this.slides.length;
      this.index = Math.max(0, this.slides.findIndex((slide) => slide.classList.contains('is-active')));
      this.delay = (parseFloat(this.dataset.speed) || 5) * 1000;
      this.pauses = new Set();

      this.watchNotch();
      if (this.count < 2) return;

      if (this.dataset.autoplay !== 'true') this.pauses.add('off');
      else if (reduceMotion.matches) this.pauses.add('user');

      this.bind();
      this.syncToggle();
      this.syncLive();

      // Warm the second slide once the page has finished loading, so the
      // first autoplay step never shows an empty frame.
      const armNext = () => idle(() => this.arm(1));
      if (document.readyState === 'complete') armNext();
      else window.addEventListener('load', armNext, { once: true });

      this.schedule();
    }

    disconnectedCallback() {
      clearTimeout(this.timer);
      clearTimeout(this.leaveTimer);
      this.viewIO?.disconnect();
      this.notchIO?.disconnect();
      window.removeEventListener('resize', this.onResize);
      document.removeEventListener('visibilitychange', this.onVisibility);
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
      document.removeEventListener('shopify:block:deselect', this.onBlockDeselect);
      if (this.notch) document.documentElement.removeAttribute('data-ctr-cta-low');
    }

    /* ------------------------------------------------------------------ */
    /* Wiring                                                              */
    /* ------------------------------------------------------------------ */

    bind() {
      this.dots.forEach((dot, i) => dot.addEventListener('click', () => this.goTo(i)));

      this.querySelector('[data-ctr-prev]')?.addEventListener('click', () => this.goTo(this.index - 1));
      this.querySelector('[data-ctr-next]')?.addEventListener('click', () => this.goTo(this.index + 1));

      this.toggle?.addEventListener('click', () => {
        if (this.pauses.has('user')) this.resume('user');
        else this.pause('user');
        this.syncToggle();
      });

      // Hover (mouse only: on touch, pointerenter fires on tap and would
      // pause for good).
      this.addEventListener('pointerenter', (event) => {
        if (event.pointerType === 'mouse') this.pause('hover');
      });
      this.addEventListener('pointerleave', (event) => {
        if (event.pointerType === 'mouse') this.resume('hover');
      });

      this.addEventListener('focusin', () => this.pause('focus'));
      this.addEventListener('focusout', (event) => {
        if (!this.contains(event.relatedTarget)) this.resume('focus');
      });

      this.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowRight') this.goTo(this.index + 1);
        else if (event.key === 'ArrowLeft') this.goTo(this.index - 1);
        else return;
        event.preventDefault();
      });

      this.bindSwipe();

      this.onVisibility = () => (document.hidden ? this.pause('hidden') : this.resume('hidden'));
      document.addEventListener('visibilitychange', this.onVisibility);

      if ('IntersectionObserver' in window) {
        this.viewIO = new IntersectionObserver(([entry]) =>
          entry.isIntersecting ? this.resume('offscreen') : this.pause('offscreen')
        );
        this.viewIO.observe(this);
      }

      // Theme editor: selecting a slide block shows it and holds it there.
      this.onBlockSelect = (event) => {
        const i = this.slides.indexOf(event.target);
        if (i < 0) return;
        this.goTo(i, true);
        this.pause('editor');
      };
      this.onBlockDeselect = (event) => {
        if (this.slides.includes(event.target)) this.resume('editor');
      };
      document.addEventListener('shopify:block:select', this.onBlockSelect);
      document.addEventListener('shopify:block:deselect', this.onBlockDeselect);
    }

    /**
     * Horizontal swipe on the card. touch-action: pan-y in the CSS leaves
     * vertical scrolling to the browser, so a scroll gesture ends in
     * pointercancel and never counts as a swipe.
     */
    bindSwipe() {
      const area = this.live;
      if (!area) return;

      let start = null;

      area.addEventListener('pointerdown', (event) => {
        if (event.pointerType === 'mouse' && event.button !== 0) return;
        start = { x: event.clientX, y: event.clientY };
        if (event.pointerType === 'touch') this.pause('touch');
      });

      const end = (event) => {
        if (event.pointerType === 'touch') this.resume('touch');
        if (!start) return;

        const dx = event.clientX - start.x;
        const dy = event.clientY - start.y;
        start = null;

        if (event.type !== 'pointerup') return;
        if (Math.abs(dx) < SWIPE_MIN || Math.abs(dx) < Math.abs(dy) * 1.2) return;

        // Swallow the click that may follow this pointerup, and only that one:
        // a touch swipe usually produces no click at all, and a stale flag
        // would eat the next real tap.
        this.swiped = true;
        setTimeout(() => (this.swiped = false), 60);
        this.goTo(this.index + (dx < 0 ? 1 : -1));
      };

      area.addEventListener('pointerup', end);
      area.addEventListener('pointercancel', end);

      // A drag that ended on a link must not also follow it.
      area.addEventListener(
        'click',
        (event) => {
          if (!this.swiped) return;
          this.swiped = false;
          event.preventDefault();
          event.stopPropagation();
        },
        true
      );
    }

    /* ------------------------------------------------------------------ */
    /* Slides                                                              */
    /* ------------------------------------------------------------------ */

    /**
     * @param {number} target - Slide index; wraps in both directions.
     * @param {boolean} [hold] - Skip restarting the autoplay timer (editor selection).
     */
    goTo(target, hold = false) {
      const next = (target + this.count) % this.count;
      if (next === this.index) return;

      this.arm(next);
      this.arm(next + 1);

      // Which way the slide transition should travel. The raw target, not the
      // wrapped one, so stepping past the last slide still reads as forwards.
      this.dataset.dir = target > this.index ? 'next' : 'prev';

      // The outgoing slide keeps its transition while it leaves; the ones
      // merely parked off-screen jump into place without animating.
      const leaving = this.slides[this.index];
      leaving?.classList.add('is-leaving');
      clearTimeout(this.leaveTimer);
      this.leaveTimer = setTimeout(() => leaving?.classList.remove('is-leaving'), 700);

      this.slides.forEach((slide, i) => this.setActive(slide, i === next));
      this.groups.forEach((group) => group.forEach((el, i) => this.setActive(el, i === next)));
      this.dots.forEach((dot, i) => {
        if (i === next) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });

      this.index = next;
      if (!hold) this.schedule();
    }

    setActive(el, active) {
      el.classList.toggle('is-active', active);
      el.inert = !active;
      if (active) el.removeAttribute('aria-hidden');
      else el.setAttribute('aria-hidden', 'true');
    }

    /** Lets a dormant slide's image load (CSS keeps it display:none until now). */
    arm(i) {
      const slide = this.slides[(i + this.count) % this.count];
      slide?.querySelector('[data-ctr-dormant]')?.removeAttribute('data-ctr-dormant');
    }

    /* ------------------------------------------------------------------ */
    /* Autoplay                                                            */
    /* ------------------------------------------------------------------ */

    schedule() {
      clearTimeout(this.timer);
      if (this.pauses.size) return;
      this.timer = setTimeout(() => this.goTo(this.index + 1), this.delay);
    }

    pause(reason) {
      this.pauses.add(reason);
      clearTimeout(this.timer);
      this.syncLive();
    }

    resume(reason) {
      if (!this.pauses.delete(reason)) return;
      this.syncLive();
      this.schedule();
    }

    /** Announce slide changes only when the user is driving them. */
    syncLive() {
      this.live?.setAttribute('aria-live', this.pauses.size ? 'polite' : 'off');
    }

    syncToggle() {
      if (!this.toggle) return;
      const paused = this.pauses.has('user');
      this.toggle.toggleAttribute('data-paused', paused);
      this.toggle.setAttribute('aria-label', paused ? 'Play slideshow' : 'Pause slideshow');
    }

    /* ------------------------------------------------------------------ */
    /* Keep the floating WhatsApp button off the SHOP NOW notch            */
    /* ------------------------------------------------------------------ */

    /**
     * Watches a band along the bottom of the viewport as tall as the WhatsApp
     * button's zone. When the notch enters it, <html data-ctr-cta-low> lifts
     * the button (see citron-header-pill.css). The hero's CSS height keeps the
     * notch out of that band on most phones, so this is the fallback for
     * short screens.
     */
    watchNotch() {
      if (!this.notch || !('IntersectionObserver' in window)) return;

      const observe = () => {
        this.notchIO?.disconnect();
        const band = 84;
        this.notchIO = new IntersectionObserver(
          ([entry]) => document.documentElement.toggleAttribute('data-ctr-cta-low', entry.isIntersecting),
          { rootMargin: `-${Math.max(0, window.innerHeight - band)}px 0px 0px 0px` }
        );
        this.notchIO.observe(this.notch);
      };

      let resizeTimer;
      this.onResize = () => {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(observe, 200);
      };
      window.addEventListener('resize', this.onResize, { passive: true });
      observe();
    }
  }

  customElements.define('citron-slider', CitronSlider);

  // The hero's markup predates the shared engine and uses its own tag name.
  if (!customElements.get('citron-hero')) {
    customElements.define('citron-hero', class CitronHero extends CitronSlider {});
  }
})();
